<template>
  <div class="policy-container">
    <div class="policy-content">
      <h1>Privacy Policy</h1>
      <p class="last-updated">Last Updated: August 14, 2026</p>

      <section>
        <h2>1. Introduction</h2>
        <p>
          The EAE (Extracurricular Activity Excellence) Platform is operated by Extracurricular Activity Excellence
          International Pte. Ltd. ("EAE", "we", "us", or "our"). This Platform Privacy Policy explains how we
          collect, use, store, share, and protect personal information when you
          use our website, platform, application forms, programs, competitions, courses, passes, and related services.
        </p>
        <p>
          This Platform Privacy Policy supplements the EAE International
          <a href="https://www.eae.international/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          Both policies apply to the EAE Platform; this policy provides the product-specific details. By using our
          Platform or submitting information through our forms, you acknowledge the practices described in both policies.
        </p>
      </section>

      <section>
        <h2>2. Programs and Services We Operate</h2>
        <p>
          This Privacy Policy applies across EAE-operated services and program-specific pages, including but not limited to:
        </p>
        <ul>
          <li>Student, advisor, and school or institutional account registration</li>
          <li>Educational competitions and activities, including WAO, BPA, IFO, Envirothon, IPsyO, IEnvO, IBO, WEC, NIM, AM, and EAE programs</li>
          <li>Program application forms, contact forms, account forms, and registration forms</li>
          <li>Universal Pass, REPass, official courses, learning resources, and related paid services</li>
          <li>Activity portfolio tools, review entries, achievement tracking, scoring, awards, certificates, and verification services</li>
          <li>Team-based activities, advisor coordination, school administration, and institutional management features</li>
        </ul>
      </section>

      <section>
        <h2>3. Information We Collect</h2>
        <p>Depending on the service or form you use, we may collect and process the following categories of information:</p>
        <ul>
          <li><strong>Personal identification:</strong> name in native language and English, email address, gender, date of birth, phone number, country or region of residence, and account identifiers</li>
          <li><strong>Educational information:</strong> school name, school location, graduation year, grade level, academic performance, subject interests, tuition tier, and advisor or institution details</li>
          <li><strong>Program and activity data:</strong> registrations, applications, activity logs, competition entries, submissions, uploaded files, achievements, scores, awards, certificates, and review entries</li>
          <li><strong>Account and communication data:</strong> login information, OTP or password reset records, support requests, emails, reminders, notifications, and service messages</li>
          <li><strong>Payment and order data:</strong> order information, transaction history, pass or course purchases, refund-related information, and payment processing records</li>
          <li><strong>Technical data:</strong> IP address, browser information, device information, session data, cookies, analytics data, and usage logs</li>
          <li><strong>Guardian or emergency contact information:</strong> contact details submitted for minor students or required program supervision</li>
        </ul>
      </section>

      <section>
        <h2>4. How We Use Information</h2>
        <p>We use personal information to:</p>
        <ul>
          <li>Create, verify, and manage student, advisor, school admin, and institutional accounts</li>
          <li>Process applications, registrations, program participation, team assignments, and eligibility checks</li>
          <li>Provide competitions, courses, learning resources, certificates, scoring, awards, and verification services</li>
          <li>Coordinate with advisors, schools, and program organizers where necessary for participation</li>
          <li>Send operational emails, OTP codes, password reset links, reminders, notifications, updates, and extracurricular opportunities</li>
          <li>Process payments, orders, refunds, invoices, fee reductions, and related customer support</li>
          <li>Maintain platform security, prevent abuse, troubleshoot issues, and improve our services</li>
          <li>Comply with legal, regulatory, tax, accounting, dispute resolution, and enforcement obligations</li>
        </ul>
      </section>

      <section>
        <h2>5. Why We Ask for Specific Information</h2>
        <p>
          Some application and registration forms ask for information that is needed to administer student programs,
          confirm eligibility, coordinate supervision, and maintain accurate participation records. For example:
        </p>
        <ul>
          <li><strong>School name, school location, and institution information:</strong> used to confirm the student's school affiliation, apply program rules that depend on school location, coordinate school-based registrations, and support advisor or institution administration.</li>
          <li><strong>Graduation year and grade level:</strong> used to determine age- or grade-appropriate eligibility, place students in the correct participation group where applicable, and maintain accurate student records over multiple program years.</li>
          <li><strong>Guardian or emergency contact information:</strong> used when a student is a minor, when a program requires parent or guardian contact, or when urgent participation, safety, or account-related communication is necessary.</li>
          <li><strong>Advisor, teacher, or program supervisor information:</strong> used to help verify a student's status, school affiliation, and eligibility for participation, confirm required supervision, coordinate team or school participation, send program notices, and support program administration.</li>
          <li><strong>Tuition tier or related fee information:</strong> used only where relevant to assess fee reduction eligibility or registration pricing support.</li>
          <li><strong>Contact details:</strong> used to create and maintain accounts, send OTP codes, reminders, application updates, participation notices, and support responses.</li>
          <li><strong>Submitted files, entries, scores, awards, and certificates:</strong> used to evaluate participation, document achievements, issue certificates, and provide verification services.</li>
        </ul>
      </section>

      <section>
        <h2>6. Cookies and Analytics</h2>
        <p>
          We may use cookies, local storage, session data, and analytics tools to keep users signed in, maintain platform
          functionality, understand service usage, detect technical issues, and improve the Platform. You may be able to
          control cookies through your browser settings, but disabling them may affect Platform functionality.
        </p>
      </section>

      <section>
        <h2>7. How We Share Information</h2>
        <p>We may share information with:</p>
        <ul>
          <li>Competition organizers, advisors, schools, and institutional administrators for program participation and administration</li>
          <li>Payment processors, including Stripe, for transaction processing and payment-related support</li>
          <li>Cloud hosting, storage, email, analytics, and technology service providers that help us operate the Platform</li>
          <li>Professional advisors, auditors, insurers, or legal representatives where reasonably necessary</li>
          <li>Government authorities, courts, regulators, or law enforcement when required by law or to protect rights, safety, or security</li>
        </ul>
        <p>
          We do not sell personal information. Third-party services may process information under their own terms and
          privacy policies where you use or interact with those services directly.
        </p>
      </section>

      <section>
        <h2>8. International Transfers and Storage</h2>
        <p>
          Your information may be stored or processed in Singapore and other jurisdictions where EAE, our service
          providers, or program partners operate. Where information is transferred internationally, we take reasonable
          steps designed to protect it in accordance with applicable laws.
        </p>
      </section>

      <section>
        <h2>9. Data Security</h2>
        <p>
          We use reasonable technical and organizational measures designed to protect personal information from
          unauthorized access, loss, misuse, alteration, or disclosure. No online service can be guaranteed to be fully
          secure, so users should also protect their account credentials and notify us of suspected unauthorized access.
        </p>
      </section>

      <section>
        <h2>10. Data Retention</h2>
        <p>
          We retain personal information for as long as necessary to provide services, maintain records of participation,
          support certificates and verification, comply with legal obligations, resolve disputes, enforce agreements, and
          support legitimate educational or operational purposes. Retention periods may vary by program, account type, and
          record category.
        </p>
      </section>

      <section>
        <h2>11. Student and Minor Data</h2>
        <p>
          Many EAE services are designed for students. Where users are minors under applicable law, a parent, guardian,
          school, advisor, or authorized institution may need to provide consent or support participation. We use student
          data for educational, competition, account, safety, communication, and administration purposes related to EAE
          services.
        </p>
      </section>

      <section>
        <h2>12. Your Rights and Choices</h2>
        <p>
          Subject to applicable laws, you may have rights to access, correct, update, delete, restrict, or object to
          certain processing of your personal information. You may also contact us about account information, communication
          preferences, or privacy questions.
        </p>
        <p>
          To exercise privacy rights or make a request, contact us at
          <a href="mailto:contact@eae.international">contact@eae.international</a>. We may need to verify your identity
          before responding.
        </p>
      </section>

      <section>
        <h2>13. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will post the updated version on the Platform with a new
          "Last Updated" date. Your continued use of the Platform after an update means the updated policy applies to your
          use of the services.
        </p>
      </section>

      <section>
        <h2>14. Contact Information</h2>
        <p>
          If you have questions about this Privacy Policy or our privacy practices, please contact us at:
        </p>
        <p>
          <strong>Email:</strong> <a href="mailto:contact@eae.international">contact@eae.international</a><br>
          <strong>Website:</strong> eae.international
        </p>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicy",
  mounted() {
    document.title = "Privacy Policy - EAE";
  }
};
</script>

<style scoped lang="scss">
.policy-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 40px 20px;
}

.policy-content {
  max-width: 900px;
  margin: 0 auto;
  background: #ffffff;
  padding: 50px 60px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.8;
  color: #333;

  h1 {
    text-align: center;
    color: #2c3e50;
    margin-bottom: 10px;
    font-size: 32px;
  }

  .last-updated {
    text-align: center;
    color: #666;
    font-size: 14px;
    margin-bottom: 40px;
  }

  h2 {
    color: #2c3e50;
    font-size: 20px;
    margin-top: 35px;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e74c3c;
  }

  p {
    margin-bottom: 15px;
    text-align: justify;
  }

  ul {
    margin: 15px 0;
    padding-left: 25px;

    li {
      margin-bottom: 10px;
    }
  }

  a {
    color: #e74c3c;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  section {
    margin-bottom: 25px;
  }
}

@media (max-width: 768px) {
  .policy-container {
    padding: 20px 10px;
  }

  .policy-content {
    padding: 30px 25px;

    h1 {
      font-size: 24px;
    }

    h2 {
      font-size: 18px;
    }
  }
}
</style>
